import React, { useEffect, useState, useCallback } from "react";
import { useParams, Link } from "react-router-dom";
import { ArrowLeft, User, Mail, GraduationCap, ShieldCheck, Download, Users } from "lucide-react";
import { getApiUrl } from "../../config/api";
import "../../styles/DashboardLayout.css";
import "../../styles/UtopiaPages.css";

const ClassDetails: React.FC = () => {
  const { id } = useParams();
  const [classData, setClassData] = useState<any>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");

  const fetchClass = useCallback(async () => {
    if (!id) {
      setError("Identification failed: Academic class referent not found.");
      setLoading(false);
      return;
    }

    try {
      const token = localStorage.getItem("token");
      const res = await fetch(getApiUrl(`academics/classes/${id}`), {
        headers: { Authorization: `Bearer ${token}` },
      });
      const data = await res.json();
      if (!res.ok) setError(data.message || "Retrieval error.");
      else setClassData(data);
    } catch { setError("System synchronization failed."); }
    finally { setLoading(false); }
  }, [id]);

  useEffect(() => { fetchClass(); }, [fetchClass]);

  return (
    <div className="dashboard-container">
      <main className="dashboard-main">
        <div className="page-title-section">
          <div>
            <Link to="/allclasses" className="flex items-center gap-2 text-[10px] font-bold text-teal-600 uppercase tracking-widest mb-2 hover:gap-3 transition-all">
              <ArrowLeft size={14} /> Return to Directory
            </Link>
            <h1>Class Specifications</h1>
            <p className="page-subtitle">Detailed breakdown of faculty and student participation for {classData?.name || "Registry Entry"}</p>
          </div>
          <button className="utopia-btn-secondary flex items-center gap-2 py-3">
            <Download size={18} /> Export Class List
          </button>
        </div>

        {loading ? (
          <div className="p-32 text-center text-slate-300">Synchronizing academic data...</div>
        ) : error ? (
          <div className="p-20 bg-red-50 rounded-3xl border border-red-100 text-center mx-8">
            <ShieldCheck size={48} className="mx-auto text-red-300 mb-4" />
            <p className="text-red-600 font-bold">{error}</p>
          </div>
        ) : (
          <div className="p-8 pt-0 space-y-8">
            {/* Info Cards */}
            <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
              <div className="utopia-page-container p-8 flex items-center gap-6">
                <div className="w-16 h-16 bg-blue-50 text-blue-600 rounded-2xl flex items-center justify-center shadow-inner"><GraduationCap size={32} /></div>
                <div>
                  <p className="text-[10px] font-bold text-slate-400 uppercase tracking-widest">Enrollment Status</p>
                  <h4 className="text-2xl font-black text-slate-800">{classData?.students?.length || 0} Students</h4>
                </div>
              </div>
              <div className="utopia-page-container p-8 flex items-center gap-6 md:col-span-2">
                <div className="w-16 h-16 bg-teal-50 text-teal-600 rounded-2xl flex items-center justify-center shadow-inner"><User size={32} /></div>
                <div className="flex-1">
                  <p className="text-[10px] font-bold text-slate-400 uppercase tracking-widest">Assigned Faculty</p>
                  <h4 className="text-2xl font-black text-slate-800">{classData?.teacher?.name || "Unassigned"}</h4>
                  <div className="flex items-center gap-2 mt-1 text-slate-400 text-xs">
                    <Mail size={12} /> {classData?.teacher?.email || "N/A"}
                  </div>
                </div>
                <span className="utopia-badge bg-teal-50 text-teal-600 border border-teal-100 px-4 py-2 font-black text-[10px] uppercase">Primary Lead</span>
              </div>
            </div>

            {/* Students Table */}
            <div className="utopia-page-container">
              <div className="utopia-table-header p-8 flex items-center justify-between">
                <div>
                  <h2 className="text-xl font-extrabold text-slate-800">Enrollment Registry</h2>
                  <p className="text-xs text-slate-400 mt-1">Verified participants for the current academic cycle</p>
                </div>
                <div className="flex items-center gap-2 p-2 bg-slate-50 rounded-xl px-4 border border-slate-100">
                  <Users size={16} className="text-slate-400" />
                  <span className="text-xs font-bold text-slate-500">{classData?.students?.length || 0} active records</span>
                </div>
              </div>

              <table className="utopia-table">
                <thead>
                  <tr>
                    <th className="w-16">Rank</th>
                    <th>Student Identity</th>
                    <th>Contact Referent</th>
                    <th className="text-right">Metrics</th>
                  </tr>
                </thead>
                <tbody>
                  {(classData?.students || []).map((s: any, idx: number) => (
                    <tr key={s.id || idx} className="group">
                      <td className="text-[10px] font-black text-slate-300 font-mono">#{(idx + 1).toString().padStart(2, '0')}</td>
                      <td>
                        <div className="flex items-center gap-3">
                          <div className="w-9 h-9 rounded-xl bg-slate-100 group-hover:bg-blue-600 transition-all duration-300 flex items-center justify-center text-slate-400 group-hover:text-white font-bold text-xs shadow-sm">
                            {s.name.charAt(0)}
                          </div>
                          <span className="font-bold text-slate-700 group-hover:translate-x-1 transition-transform">{s.name}</span>
                        </div>
                      </td>
                      <td>
                        <div className="flex items-center gap-2 text-slate-500 text-sm">
                          <Mail size={14} className="opacity-30" />
                          {s.email}
                        </div>
                      </td>
                      <td className="text-right">
                        <Link to={`/studentperformance/${s.id}`} className="utopia-btn-secondary py-2 px-4 text-[10px] font-bold">
                          Performance Profile
                        </Link>
                      </td>
                    </tr>
                  ))}
                  {(classData?.students?.length || 0) === 0 && (
                    <tr>
                      <td colSpan={4} className="text-center py-24 text-slate-300">
                        <Users size={48} className="mx-auto mb-4 opacity-10" />
                        <p className="text-base font-medium">No active student participation recorded for this class.</p>
                      </td>
                    </tr>
                  )}
                </tbody>
              </table>
            </div>
          </div>
        )}
      </main>
    </div>
  );
};

export default ClassDetails;











